import { logger } from '@commander/logger';
import { analytics } from '@commander/product-analytics';
import { toast } from '@commander/ui';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { EmailSentNotSavedError } from '../api/emailApi';
import { EmailComposer } from '../components/email/EmailComposer';
import { useSendEmail, useUpdateDraftEmail } from '../hooks';
import type { Email, EmailTemplate, StructuredRecipient } from '../types';

interface ComposeOptions {
  recipientEmail?: string;
  recipientName?: string;
  contactId?: string;
  masterCompanyLocationId?: string;
}

interface ComposePrefillOptions {
  recipientEmail?: string;
  recipientName?: string;
  contactId?: string;
  masterCompanyLocationId?: string;
  subject?: string;
  bodyLexical?: any;
  attachments?: Array<{ id: string; fileName: string; fileType: string; fileSize: number }>;
  onSendSuccess?: (sentEmail: Email) => void | Promise<void>;
  quoteId?: string;
  signingUrl?: string | null;
  quoteConfirmData?: {
    auto_create_payment_link?: boolean;
    bank_account_id?: string;
    attachmentId?: string;
  };
}

interface ComposeToContactOptions {
  email: string;
  name?: string;
  contactId: string;
  masterCompanyLocationId?: string;
  // Auto-runs AI generation with this prompt and applies the result, instead
  // of leaving the composer blank for the rep to write or generate manually.
  autoGeneratePrompt?: string;
}

interface ReplyOptions {
  autoGeneratePrompt?: string;
}

interface ComposeToContactsOptions {
  contacts: Array<{
    email: string;
    name?: string;
    contactId: string;
  }>;
  disableRecipientEditing?: boolean;
  masterCompanyLocationId?: string;
}

interface EmailComposerContextValue {
  compose: (options?: ComposeOptions) => void;
  composeToContact: (contact: ComposeToContactOptions) => void;
  composeToContacts: (options: ComposeToContactsOptions) => void;
  composeWithTemplate: (template: EmailTemplate) => void;
  composeWithPrefill: (options: ComposePrefillOptions) => void;
  reply: (email: Email, replyAll?: boolean, options?: ReplyOptions) => void;
  editDraft: (draft: Email) => void;
  close: () => void;
}

const EmailComposerContext = createContext<EmailComposerContextValue | null>(null);

export function EmailComposerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [recipientName, setRecipientName] = useState<string>('');
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [structuredRecipients, setStructuredRecipients] = useState<
    StructuredRecipient[] | undefined
  >(undefined);
  const [disableRecipientEditing, setDisableRecipientEditing] = useState(false);
  const [template, setTemplate] = useState<EmailTemplate | undefined>(undefined);
  const [replyToEmail, setReplyToEmail] = useState<Email | undefined>(undefined);
  const [isReplyAll, setIsReplyAll] = useState(false);
  const [draftEmail, setDraftEmail] = useState<Email | undefined>(undefined);
  const [masterCompanyLocationId, setMasterCompanyLocationId] = useState<string | undefined>(
    undefined
  );
  const [initialSubject, setInitialSubject] = useState<string>('');
  const [initialAttachments, setInitialAttachments] = useState<
    Array<{ id: string; name: string; size: number; type: string; url: string; uploadedAt: Date }>
  >([]);
  const [initialBodyLexical, setInitialBodyLexical] = useState<any>(null);
  const [onSendSuccessCallback, setOnSendSuccessCallback] = useState<
    ((sentEmail: Email) => void | Promise<void>) | null
  >(null);
  const [quoteMetadata, setQuoteMetadata] = useState<{
    quoteId: string;
    signingUrl?: string | null;
    quoteConfirmData?: {
      auto_create_payment_link?: boolean;
      bank_account_id?: string;
      attachmentId?: string;
    };
  } | null>(null);
  const [autoGeneratePrompt, setAutoGeneratePrompt] = useState<string | undefined>(undefined);
  // Distinct from autoGeneratePrompt's text: two different requests (e.g. two
  // Next Steps actions) can carry the same templated prompt text, and the
  // composer's "already triggered" guard keys off this instead so the second
  // request still fires when the composer stays mounted between them.
  const [autoGenerateKey, setAutoGenerateKey] = useState<string | undefined>(undefined);

  const sendEmailMutation = useSendEmail();
  const updateDraftEmailMutation = useUpdateDraftEmail();

  const resetState = () => {
    setRecipientEmail('');
    setRecipientName('');
    setContactId(undefined);
    setStructuredRecipients(undefined);
    setDisableRecipientEditing(false);
    setTemplate(undefined);
    setReplyToEmail(undefined);
    setIsReplyAll(false);
    setDraftEmail(undefined);
    setMasterCompanyLocationId(undefined);
    setInitialSubject('');
    setInitialAttachments([]);
    setInitialBodyLexical(null);
    setOnSendSuccessCallback(null);
    setQuoteMetadata(null);
    setAutoGeneratePrompt(undefined);
    setAutoGenerateKey(undefined);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  const handleSend = async (emailData: any, draftId?: string) => {
    try {
      let sentEmail: Email | undefined;

      // Merge quote metadata into email data so the backend can confirm the quote
      const emailDataWithQuote = quoteMetadata
        ? {
            ...emailData,
            quoteId: quoteMetadata.quoteId,
            quoteConfirmData: quoteMetadata.quoteConfirmData,
          }
        : emailData;

      if (draftId) {
        // Editing existing draft
        if (emailDataWithQuote.isDraft) {
          // Save draft updates only
          await updateDraftEmailMutation.mutateAsync({
            id: draftId,
            data: emailDataWithQuote,
          });
          toast.success('Draft updated successfully');
        } else {
          // Send the draft (backend will auto-delete draft)
          sentEmail = await sendEmailMutation.mutateAsync({
            ...emailDataWithQuote,
            isDraft: false,
            sourceDraftId: draftId,
          });
          toast.success('Email sent successfully');

          // Track email sent event
          const recipientCount = emailData.to?.length || emailData.recipients?.length || 0;
          analytics.track('email_sent' as any, {
            recipient_count: recipientCount,
            attachment_count: emailData.attachments?.length || 0,
          });
        }
      } else {
        // Creating new email/draft
        sentEmail = await sendEmailMutation.mutateAsync(emailDataWithQuote);
        toast.success(
          emailDataWithQuote.isDraft ? 'Draft saved successfully' : 'Email sent successfully'
        );

        // Track email sent event for new emails
        if (!emailDataWithQuote.isDraft) {
          const recipientCount =
            emailDataWithQuote.to?.length || emailDataWithQuote.recipients?.length || 0;
          analytics.track('email_sent' as any, {
            recipient_count: recipientCount,
            attachment_count: emailData.attachments?.length || 0,
          });
        }
      }

      // Invoke onSendSuccess callback after successful non-draft send
      if (!emailDataWithQuote.isDraft && onSendSuccessCallback && sentEmail) {
        try {
          await onSendSuccessCallback(sentEmail);
        } catch (callbackError) {
          logger.error('Post-send callback failed', callbackError);
          toast.warning('Email sent, but a follow-up action failed. Please refresh the page.');
        }
      }

      handleClose();
    } catch (error: any) {
      if (error instanceof EmailSentNotSavedError) {
        logger.error('Email sent but not saved', { emailId: error.emailId });
        toast.warning(error.message);
        handleClose();
        return;
      }
      logger.error('Failed to send email', error);
      throw error; // Re-throw to let EmailComposer handle it
    }
  };

  const compose = (options?: ComposeOptions) => {
    resetState();
    if (options) {
      setRecipientEmail(options.recipientEmail || '');
      setRecipientName(options.recipientName || '');
      setContactId(options.contactId);
      setMasterCompanyLocationId(options.masterCompanyLocationId);
    }

    // Track email composed event
    analytics.track('email_composed' as any, {
      recipient_count: options?.recipientEmail ? 1 : 0,
      related_contact: options?.contactId,
    });

    setIsOpen(true);
  };

  const composeToContact = (contact: ComposeToContactOptions) => {
    resetState();
    const recipients: StructuredRecipient[] = [
      {
        address: contact.email,
        name: contact.name,
        tenantContactId: contact.contactId,
      },
    ];
    setStructuredRecipients(recipients);
    setContactId(contact.contactId);
    setRecipientName(contact.name || '');
    setDisableRecipientEditing(true);

    // Track email composed event
    analytics.track('email_composed' as any, {
      recipient_count: 1,
      related_contact: contact.contactId,
    });

    setMasterCompanyLocationId(contact.masterCompanyLocationId);
    setAutoGeneratePrompt(contact.autoGeneratePrompt);
    setAutoGenerateKey(contact.autoGeneratePrompt ? crypto.randomUUID() : undefined);
    setIsOpen(true);
  };

  const composeToContacts = (options: ComposeToContactsOptions) => {
    resetState();
    const recipients: StructuredRecipient[] = options.contacts.map((c) => ({
      address: c.email,
      name: c.name,
      tenantContactId: c.contactId,
    }));
    setStructuredRecipients(recipients);
    // Use first contact for AI generation context
    if (options.contacts.length > 0) {
      setContactId(options.contacts[0].contactId);
      setRecipientName(options.contacts[0].name || '');
    }
    setDisableRecipientEditing(options.disableRecipientEditing ?? recipients.length === 1);

    // Track email composed event
    analytics.track('email_composed' as any, {
      recipient_count: options.contacts.length,
    });

    setMasterCompanyLocationId(options.masterCompanyLocationId);
    setIsOpen(true);
  };

  const composeWithTemplate = (emailTemplate: EmailTemplate) => {
    resetState();
    setTemplate(emailTemplate);

    // Track email template used event
    analytics.track('email_template_used', {
      template_name: emailTemplate.name,
      template_category: emailTemplate.category,
    });

    // Also track email composed when using template
    analytics.track('email_composed' as any, {
      template_used: emailTemplate.name,
    });

    setIsOpen(true);
  };

  const composeWithPrefill = (options: ComposePrefillOptions) => {
    resetState();
    if (options.recipientEmail) {
      setRecipientEmail(options.recipientEmail);
    }
    if (options.recipientName) {
      setRecipientName(options.recipientName);
    }
    setContactId(options.contactId);
    setMasterCompanyLocationId(options.masterCompanyLocationId);

    if (options.subject) {
      setInitialSubject(options.subject);
    }
    if (options.attachments && options.attachments.length > 0) {
      setInitialAttachments(
        options.attachments.map((a) => ({
          id: a.id,
          name: a.fileName,
          size: a.fileSize,
          type: a.fileType,
          url: '',
          uploadedAt: new Date(),
        }))
      );
    }
    if (options.bodyLexical) {
      setInitialBodyLexical(options.bodyLexical);
    }
    if (options.onSendSuccess) {
      // Wrap in arrow function to prevent React from calling it as a state updater
      setOnSendSuccessCallback(
        () => options.onSendSuccess as (sentEmail: Email) => void | Promise<void>
      );
    }
    if (options.quoteId) {
      setQuoteMetadata({
        quoteId: options.quoteId,
        signingUrl: options.signingUrl ?? null,
        quoteConfirmData: options.quoteConfirmData,
      });
    }

    analytics.track('email_composed' as any, {
      recipient_count: options.recipientEmail ? 1 : 0,
      related_contact: options.contactId,
      prefilled: true,
    });

    setIsOpen(true);
  };

  const reply = (email: Email, replyAll = false, options?: ReplyOptions) => {
    resetState();
    setReplyToEmail(email);
    setIsReplyAll(replyAll);
    setAutoGeneratePrompt(options?.autoGeneratePrompt);
    setAutoGenerateKey(options?.autoGeneratePrompt ? crypto.randomUUID() : undefined);

    // Track email replied event
    analytics.track('email_replied' as any, {
      email_id: email.id,
      sender: email.from,
      subject: email.subject,
    });

    setIsOpen(true);
  };

  const editDraft = (draft: Email) => {
    resetState();
    setDraftEmail(draft);
    setIsOpen(true);
  };

  const value: EmailComposerContextValue = {
    compose,
    composeToContact,
    composeToContacts,
    composeWithTemplate,
    composeWithPrefill,
    reply,
    editDraft,
    close: handleClose,
  };

  return (
    <EmailComposerContext.Provider value={value}>
      {children}
      <EmailComposer
        isOpen={isOpen}
        onClose={handleClose}
        onSend={handleSend}
        recipientEmail={recipientEmail}
        recipientName={recipientName}
        contactId={contactId}
        structuredRecipients={structuredRecipients}
        disableRecipientEditing={disableRecipientEditing}
        template={template}
        replyToEmail={replyToEmail}
        isReplyAll={isReplyAll}
        draftEmail={draftEmail}
        masterCompanyLocationId={masterCompanyLocationId}
        initialSubject={initialSubject}
        initialAttachments={initialAttachments}
        initialBodyLexical={initialBodyLexical}
        quoteContext={
          quoteMetadata
            ? { quoteId: quoteMetadata.quoteId, signingUrl: quoteMetadata.signingUrl ?? null }
            : undefined
        }
        autoGeneratePrompt={autoGeneratePrompt}
        autoGenerateKey={autoGenerateKey}
      />
    </EmailComposerContext.Provider>
  );
}

export function useEmailComposer(): EmailComposerContextValue {
  const context = useContext(EmailComposerContext);
  if (!context) {
    throw new Error('useEmailComposer must be used within EmailComposerProvider');
  }
  return context;
}

export function useOptionalEmailComposer(): EmailComposerContextValue | null {
  return useContext(EmailComposerContext);
}
